import { StateAliases } from '@helo/cms'

/**
 * Metadata property names to strip recursively from tableConfig before persisting.
 * These bloat the payload and can cause 413/500 (request entity too large).
 */
const METADATA_KEYS = new Set(['__typename'])

const stripMetadataFromValue = (value: unknown): unknown => {
  if (value === null || value === undefined) {
    return value
  }
  if (Array.isArray(value)) {
    return value.map(stripMetadataFromValue)
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(obj)) {
      if (METADATA_KEYS.has(k)) continue
      out[k] = stripMetadataFromValue(v)
    }
    return out
  }
  return value
}

/**
 * Reduces a column config to only persistable fields: key, selector, state,
 * and config.state. Full DataTypeConfig trees are re-resolved when loading.
 */
const stripColumnConfigForPersist = (
  column: Record<string, unknown>,
): Record<string, unknown> => {
  const config = column.config as Record<string, unknown> | undefined
  const result: Record<string, unknown> = {
    key: column.key,
    selector: stripMetadataFromValue(column.selector),
    ...(column.state != null && {
      state: stripMetadataFromValue(column.state),
    }),
  }
  if (
    config != null &&
    typeof config === 'object' &&
    config.state !== undefined
  ) {
    result.config = { state: stripMetadataFromValue(config.state) }
  }
  return result
}

/**
 * Recursively strips metadata from tableConfig and shrinks columnConfigs
 * so only key, selector, and config.state are persisted. Use before saving
 * a saved view to avoid request entity too large (413/500).
 */
export const stripTableConfigMetadata = (tableConfig: unknown): unknown => {
  if (tableConfig === null || tableConfig === undefined) {
    return tableConfig
  }
  if (typeof tableConfig !== 'object') {
    return tableConfig
  }
  // Emit canonical state keys when persisting nested column/config state.
  const config = StateAliases.normalizePersistedStateTree(
    tableConfig,
  ) as Record<string, unknown>
  const columnConfigs = config.columnConfigs
  const base = stripMetadataFromValue(config) as Record<string, unknown>
  if (Array.isArray(columnConfigs)) {
    base.columnConfigs = columnConfigs.map(col =>
      stripColumnConfigForPersist(
        typeof col === 'object' && col !== null
          ? (col as Record<string, unknown>)
          : {},
      ),
    )
  }
  return base
}

/**
 * Strips metadata and shrinks columnConfigs for query export input.
 * Use before calling the queryExport mutation to avoid request entity too large (413/500).
 */
export const stripQueryExportInput = (
  input: Record<string, unknown>,
): Record<string, unknown> => {
  const result = StateAliases.normalizeExportInput({ ...input })
  if (Array.isArray(result.columnConfigs)) {
    result.columnConfigs = result.columnConfigs.map(col =>
      stripColumnConfigForPersist(
        typeof col === 'object' && col !== null
          ? (col as Record<string, unknown>)
          : {},
      ),
    )
  }
  if (result.columns != null) {
    result.columns = stripMetadataFromValue(result.columns)
  }
  if (result.connectionLocator != null) {
    result.connectionLocator = stripMetadataFromValue(result.connectionLocator)
  }
  if (result.variables != null) {
    result.variables = stripMetadataFromValue(result.variables)
  }
  return result
}
